import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import {
  Plus, ListChecks, Warning, PencilSimple, Trash, Flag,
} from '@phosphor-icons/react';
import { format, parseISO } from 'date-fns';

const STATUS_COLORS = {
  completed: { bar: 'bg-emerald-500', text: 'text-emerald-700', bg: 'bg-emerald-50', label: 'Terminée' },
  in_progress: { bar: 'bg-blue-500', text: 'text-blue-700', bg: 'bg-blue-50', label: "En cours d'execution" },
  pending: { bar: 'bg-amber-400', text: 'text-amber-700', bg: 'bg-amber-50', label: 'A realiser' },
  delayed: { bar: 'bg-red-500', text: 'text-red-700', bg: 'bg-red-50', label: 'En retard critique' },
};

function DelayBadge() {
  return (
    <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-red-500 text-white font-bold animate-pulse whitespace-nowrap" data-testid="delay-badge">
      <Warning size={10} weight="fill" /> RETARD CRITIQUE
    </span>
  );
}

function getTaskCardBorder(task) {
  if (task.task_type === 'milestone') return 'border-l-[#FF6B00] bg-orange-50/30';
  if (task.status === 'delayed') return 'border-l-red-500 bg-red-50/30';
  if (task.status === 'completed') return 'border-l-emerald-500';
  if (task.status === 'in_progress') return 'border-l-blue-500';
  return 'border-l-amber-400';
}

export function TaskListView({ filteredTasks, getChantierName, onEditTask, onDeleteConfirm, onCreateSheet }) {
  if (filteredTasks.length === 0) {
    return (
      <Card className="border border-dashed">
        <CardContent className="py-12 text-center">
          <ListChecks size={48} className="mx-auto mb-3 text-gray-300" />
          <h3 className="font-bold text-[#111827] mb-1">Aucune tâche planifiee</h3>
          <p className="text-sm text-[#6B7280] mb-4">Commencez par créer votre premiere tâche</p>
          <Button onClick={() => onCreateSheet()} className="bg-[#FF6B00] hover:bg-orange-700">
            <Plus size={16} className="mr-1.5" /> Nouvelle tâche
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {filteredTasks.map(task => {
        const sc = STATUS_COLORS[task.status] || STATUS_COLORS.pending;
        const progress = task.progress || (task.status === 'completed' ? 100 : 0);
        const isMilestone = task.task_type === 'milestone';
        const isDelayed = task.status === 'delayed';
        return (
          <Card key={task.id} className={`border-l-4 ${getTaskCardBorder(task)}`} data-testid={`task-card-${task.id}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    {isMilestone && <Flag size={16} weight="fill" className="text-[#FF6B00]" />}
                    <h3 className="font-bold text-sm text-[#111827]">{task.title}</h3>
                    <span className={`text-[10px] px-2 py-0.5 rounded font-bold ${sc.bg} ${sc.text}`}>
                      {isMilestone ? 'Jalon' : sc.label}
                    </span>
                    {isDelayed && <DelayBadge />}
                  </div>
                  {task.description && <p className="text-xs text-[#4B5563] mb-2">{task.description}</p>}
                  <div className="flex flex-wrap gap-3 text-xs text-[#6B7280]">
                    <span>{task.start_date && format(parseISO(task.start_date), 'dd/MM')}{!isMilestone && task.end_date && ` - ${format(parseISO(task.end_date), 'dd/MM/yyyy')}`}</span>
                    <span className="text-[#FF6B00] font-semibold">{getChantierName(task.chantier_id)}</span>
                    {task.lot && <span>Lot: {task.lot}</span>}
                    {task.responsible && <span>{task.responsible}</span>}
                  </div>
                  {!isMilestone && (
                    <div className="flex items-center gap-2 mt-2">
                      <div className="flex-1 h-3 bg-gray-100 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full transition-all ${sc.bar}`} style={{ width: `${progress}%` }} />
                      </div>
                      <span className={`text-[11px] font-black w-10 text-right ${
                        progress >= 75 ? 'text-emerald-600' : progress >= 40 ? 'text-[#FF6B00]' : 'text-[#6B7280]'
                      }`}>{progress}%</span>
                    </div>
                  )}
                </div>
                <div className="flex gap-1.5 flex-shrink-0">
                  <Button variant="outline" size="icon" onClick={() => onEditTask(task)} className="h-10 w-10"
                    data-testid={`edit-task-${task.id}`}>
                    <PencilSimple size={16} />
                  </Button>
                  <Button variant="outline" size="icon" onClick={() => onDeleteConfirm(task)}
                    className="h-10 w-10 text-red-600 hover:bg-red-50" data-testid={`delete-task-${task.id}`}>
                    <Trash size={16} />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
